import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import {
  ArrowDownTrayIcon,
  ArchiveBoxIcon,
  ArrowPathIcon,
  CheckIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  ShareIcon,
  TagIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { io } from "socket.io-client";
import api from "../services/api";
import PaginationPage from "./PaginationPage";
import TagAssignmentDialog from "./TagAssignmentDialog";
import TagBadge from "./TagBadge";
import TagManagerDialog from "./TagManagerDialog";

const apiBaseUrl = process.env.REACT_APP_URL_LOCAL || "";
const itemsPerPage = 10;

const formatSize = (bytes) => {
  const value = Number(bytes || 0);
  if (!value) return "0 o";
  const units = ["o", "Ko", "Mo", "Go", "To"];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  return `${(value / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function StockagePage() {
  const [elements, setElements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [trashOnly, setTrashOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [form, setForm] = useState({ Nom: "", Description: "", file: null });
  const [editingElement, setEditingElement] = useState(null);
  const [editForm, setEditForm] = useState({ Nom: "", Description: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [pendingHardDelete, setPendingHardDelete] = useState(null);
  const [hardDeleting, setHardDeleting] = useState(false);
  const [sharingElement, setSharingElement] = useState(null);
  const [shareLink, setShareLink] = useState(null);
  const [shareMaximum, setShareMaximum] = useState(1);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareSaving, setShareSaving] = useState(false);
  const [shareError, setShareError] = useState("");
  const [shareCopied, setShareCopied] = useState(false);
  const [shareDownloads, setShareDownloads] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [tagManagerOpen, setTagManagerOpen] = useState(false);
  const [taggingElement, setTaggingElement] = useState(null);

  const loadElements = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await api.get(`/stockage?deletedOnly=${trashOnly}`);
      setElements(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Erreur chargement stockage :", err);
      setError("Impossible de charger les fichiers.");
    } finally {
      setLoading(false);
    }
  }, [trashOnly]);

  const loadTags = useCallback(async () => {
    try {
      setTagsLoading(true);
      const response = await api.get("/stockage/tags");
      setTags(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Erreur chargement tags :", err);
      setError("Impossible de charger les tags.");
    } finally {
      setTagsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadElements();
  }, [loadElements]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  const filteredElements = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return elements;
    return elements.filter((element) =>
      [
        element.Nom,
        element.Description,
        element.MimeType,
        element.Utilisateur?.Surnom,
        ...(element.Tags || []).map((tag) => tag.Nom),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle))
    );
  }, [elements, search]);

  const totalSize = useMemo(
    () => filteredElements.reduce((total, element) => total + Number(element.Taille || 0), 0),
    [filteredElements]
  );

  const totalPages = Math.max(1, Math.ceil(filteredElements.length / itemsPerPage));
  const paginatedElements = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredElements.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, filteredElements]);

  useEffect(() => {
    setCurrentPage(1);
  }, [trashOnly, search]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  useEffect(() => {
    const storageElementId = sharingElement?.StockageElementID;
    const token = localStorage.getItem("token");
    if (!storageElementId || !token) return undefined;

    const socket = io(apiBaseUrl || window.location.origin, {
      auth: { token },
      transports: ["websocket", "polling"],
    });

    socket.on("connect", () => socket.emit("watch-share-link", storageElementId));
    socket.on("share-link-state", (state) => {
      if (Number(state.StockageElementID) !== Number(storageElementId)) return;
      setShareLink((current) => current
        ? { ...current, ...state, url: state.destroyed ? null : current.url }
        : { ...state, url: null });
    });
    socket.on("share-download-progress", (download) => {
      if (Number(download.StockageElementID) !== Number(storageElementId)) return;
      setShareDownloads((current) => {
        const next = current.filter((item) => item.downloadId !== download.downloadId);
        return [download, ...next].slice(0, 10);
      });
    });

    return () => {
      socket.emit("unwatch-share-link", storageElementId);
      socket.disconnect();
    };
  }, [sharingElement?.StockageElementID]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.file) {
      setError("Choisis un fichier à stocker.");
      return;
    }

    const data = new FormData();
    data.append("Nom", form.Nom || form.file.name);
    data.append("Description", form.Description);
    data.append("file", form.file);

    try {
      setSaving(true);
      setError("");
      setSuccess("");
      await api.post("/stockage", data);
      setForm({ Nom: "", Description: "", file: null });
      setSuccess("Fichier ajouté.");
      await loadElements();
    } catch (err) {
      console.error("Erreur upload stockage :", err);
      setError(err.response?.data?.error || "Impossible d'ajouter le fichier.");
    } finally {
      setSaving(false);
    }
  };

  const handleSoftDelete = async (id) => {
    await api.delete(`/stockage/${id}`);
    await loadElements();
  };

  const handleRestore = async (id) => {
    await api.put(`/stockage/${id}/restore`);
    await loadElements();
  };

  const handlePrivateDownload = async (element) => {
    try {
      setError("");
      const response = await api.post(`/stockage/${element.StockageElementID}/download-ticket`);
      const url = response.data?.url;
      if (!url) throw new Error("URL de téléchargement absente.");

      const anchor = document.createElement("a");
      anchor.href = url;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } catch (err) {
      console.error("Erreur téléchargement stockage :", err);
      setError(err.response?.data?.error || "Impossible de télécharger le fichier.");
    }
  };

  const openShareModal = async (element) => {
    setSharingElement(element);
    setShareLink(null);
    setShareMaximum(1);
    setShareDownloads([]);
    setShareError("");
    setShareCopied(false);
    try {
      setShareLoading(true);
      const response = await api.get(`/stockage/${element.StockageElementID}/share`);
      const link = response.data?.link || null;
      setShareLink(link);
      if (link?.maximum) setShareMaximum(link.maximum);
    } catch (err) {
      console.error("Erreur chargement lien de partage :", err);
      setShareError(err.response?.data?.error || "Impossible de charger le lien de partage.");
    } finally {
      setShareLoading(false);
    }
  };

  const closeShareModal = () => {
    setSharingElement(null);
    setShareLink(null);
    setShareDownloads([]);
    setShareError("");
    setShareCopied(false);
  };

  const generateShareLink = async (event) => {
    event.preventDefault();
    if (!sharingElement?.StockageElementID) return;

    const maximum = Number(shareMaximum);
    if (!Number.isInteger(maximum) || maximum < 1 || maximum > 1000) {
      setShareError("Le maximum doit être compris entre 1 et 1000.");
      return;
    }

    try {
      setShareSaving(true);
      setShareError("");
      setShareCopied(false);
      setShareDownloads([]);
      const response = await api.post(`/stockage/${sharingElement.StockageElementID}/share`, {
        maximum,
      });
      setShareLink(response.data?.link || null);
    } catch (err) {
      console.error("Erreur génération lien de partage :", err);
      setShareError(err.response?.data?.error || "Impossible de générer le lien.");
    } finally {
      setShareSaving(false);
    }
  };

  const revokeShareLink = async () => {
    if (!sharingElement?.StockageElementID) return;
    try {
      setShareSaving(true);
      setShareError("");
      await api.delete(`/stockage/${sharingElement.StockageElementID}/share`);
      setShareLink(null);
      setShareDownloads([]);
    } catch (err) {
      console.error("Erreur révocation lien de partage :", err);
      setShareError(err.response?.data?.error || "Impossible de détruire le lien.");
    } finally {
      setShareSaving(false);
    }
  };

  const copyShareLink = async () => {
    if (!shareLink?.url) return;
    try {
      await navigator.clipboard.writeText(shareLink.url);
      setShareCopied(true);
      window.setTimeout(() => setShareCopied(false), 2000);
    } catch {
      setShareError("Impossible de copier automatiquement le lien.");
    }
  };

  const openEditModal = (element) => {
    setEditingElement(element);
    setEditForm({
      Nom: element.Nom || "",
      Description: element.Description || "",
    });
    setError("");
    setSuccess("");
  };

  const closeEditModal = () => {
    setEditingElement(null);
    setEditForm({ Nom: "", Description: "" });
  };

  const handleUpdateElement = async (event) => {
    event.preventDefault();
    if (!editingElement?.StockageElementID) return;

    const nom = editForm.Nom.trim();
    if (!nom) {
      setError("Le nom du fichier est requis.");
      return;
    }

    try {
      setEditSaving(true);
      setError("");
      setSuccess("");
      await api.put(`/stockage/${editingElement.StockageElementID}`, {
        Nom: nom,
        Description: editForm.Description,
      });
      setSuccess("Données du fichier mises à jour.");
      closeEditModal();
      await loadElements();
    } catch (err) {
      console.error("Erreur mise à jour stockage :", err);
      setError(err.response?.data?.error || "Impossible de mettre à jour le fichier.");
    } finally {
      setEditSaving(false);
    }
  };

  const openHardDeleteModal = (element) => {
    setPendingHardDelete(element);
    setError("");
    setSuccess("");
  };

  const closeHardDeleteModal = () => {
    setPendingHardDelete(null);
  };

  const confirmHardDelete = async () => {
    if (!pendingHardDelete?.StockageElementID) return;

    try {
      setHardDeleting(true);
      setError("");
      setSuccess("");
      await api.delete(`/stockage/${pendingHardDelete.StockageElementID}/hard`);
      setSuccess("Fichier supprimé définitivement.");
      closeHardDeleteModal();
      await loadElements();
    } catch (err) {
      console.error("Erreur suppression définitive stockage :", err);
      setError(err.response?.data?.error || "Impossible de supprimer définitivement le fichier.");
    } finally {
      setHardDeleting(false);
    }
  };

  const handleTagsChanged = useCallback(async () => {
    await Promise.all([loadTags(), loadElements()]);
  }, [loadElements, loadTags]);

  const openTagManagerFromAssignment = () => {
    setTaggingElement(null);
    setTagManagerOpen(true);
  };

  return (
    <div className="mx-auto max-w-6xl py-10">
      <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-bold uppercase text-sky-500 dark:text-sky-400">Stockage</p>
          <h1 className="mt-2 text-3xl font-black text-slate-950 dark:text-white">Fichiers stockés</h1>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={() => setTagManagerOpen(true)}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-violet-400/30 bg-white/75 px-4 py-3 text-sm font-bold text-violet-700 shadow-sm transition hover:bg-violet-500/10 dark:bg-slate-950/60 dark:text-violet-200"
          >
            <TagIcon className="size-5" />
            Gérer mes tags
          </button>
          <label className="inline-flex items-center gap-3 rounded-xl border border-sky-500/20 bg-white/75 px-4 py-3 text-sm font-bold text-slate-700 shadow-sm dark:bg-slate-950/60 dark:text-slate-200">
            <input
              type="checkbox"
              checked={trashOnly}
              onChange={(event) => setTrashOnly(event.target.checked)}
              className="size-4 accent-sky-500"
            />
            Afficher la corbeille
          </label>
        </div>
      </header>

      <section className="mb-8 rounded-2xl border border-sky-500/10 bg-white/85 p-6 shadow-xl shadow-slate-950/5 backdrop-blur dark:bg-slate-950/70">
        <form onSubmit={handleSubmit} className="grid gap-4 lg:grid-cols-[1fr_1fr_auto] lg:items-end">
          <div>
            <label className="text-sm font-bold text-slate-700 dark:text-slate-200">Nom</label>
            <input
              type="text"
              value={form.Nom}
              onChange={(event) => setForm((current) => ({ ...current, Nom: event.target.value }))}
              placeholder="Nom affiché"
              className="mt-2 w-full rounded-xl border border-sky-500/20 bg-white px-4 py-3 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-sky-400 dark:bg-slate-950/70 dark:text-white"
            />
          </div>
          <div>
            <label className="text-sm font-bold text-slate-700 dark:text-slate-200">Description</label>
            <input
              type="text"
              value={form.Description}
              onChange={(event) => setForm((current) => ({ ...current, Description: event.target.value }))}
              placeholder="Description optionnelle"
              className="mt-2 w-full rounded-xl border border-sky-500/20 bg-white px-4 py-3 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-sky-400 dark:bg-slate-950/70 dark:text-white"
            />
          </div>
          <div className="grid gap-2 sm:grid-cols-[1fr_auto] lg:grid-cols-1">
            <input
              type="file"
              onChange={(event) => setForm((current) => ({ ...current, file: event.target.files?.[0] || null }))}
              className="block w-full text-sm font-semibold text-slate-700 file:mr-4 file:rounded-lg file:border-0 file:bg-sky-500 file:px-4 file:py-3 file:text-sm file:font-bold file:text-white hover:file:bg-sky-600 dark:text-slate-200"
            />
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-sky-500 px-5 py-3 text-sm font-bold text-white transition hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <ArchiveBoxIcon className="size-5" />
              {saving ? "Ajout..." : "Ajouter"}
            </button>
          </div>
        </form>

        {error && <p className="mt-4 text-sm font-semibold text-red-600 dark:text-red-300">{error}</p>}
        {success && <p className="mt-4 text-sm font-semibold text-emerald-600 dark:text-emerald-300">{success}</p>}
      </section>

      <section className="rounded-2xl border border-sky-500/10 bg-white/85 shadow-xl shadow-slate-950/5 backdrop-blur dark:bg-slate-950/70">
        <div className="flex flex-col gap-3 border-b border-sky-500/10 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center">
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Rechercher un fichier"
              className="w-full rounded-xl border border-sky-500/20 bg-white px-4 py-3 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-sky-400 dark:bg-slate-950/70 dark:text-white sm:max-w-sm"
            />
            <p className="shrink-0 text-sm font-bold text-slate-600 dark:text-slate-300">
              Poids total : <span className="text-sky-600 dark:text-sky-400">{formatSize(totalSize)}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={loadElements}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-sky-300/40 px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-sky-500/10 dark:text-slate-200"
          >
            <ArrowPathIcon className="size-5" />
            Actualiser
          </button>
        </div>

        {loading ? (
          <div className="p-6 text-sm font-semibold text-slate-500 dark:text-slate-300">Chargement...</div>
        ) : filteredElements.length === 0 ? (
          <div className="p-6 text-sm font-semibold text-slate-500 dark:text-slate-300">
            {trashOnly ? "La corbeille est vide." : "Aucun fichier."}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-sky-500/10 text-sm">
              <thead className="bg-slate-50/80 text-left text-xs font-black uppercase text-slate-500 dark:bg-slate-900/70 dark:text-slate-300">
                <tr>
                  <th className="px-4 py-3">Nom</th>
                  <th className="px-4 py-3">Tags</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Taille</th>
                  <th className="px-4 py-3">Ajout</th>
                  <th className="px-4 py-3">État</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
                <tbody className="divide-y divide-sky-500/10 text-slate-700 dark:text-slate-100">
                  {paginatedElements.map((element) => (
                  <tr key={element.StockageElementID}>
                    <td className="px-4 py-3">
                      <div className="font-bold">{element.Nom}</div>
                      {element.Description && <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{element.Description}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {(element.Tags || []).length > 0 ? (
                        <div className="flex min-w-32 flex-wrap gap-1.5">
                          {element.Tags.map((tag) => <TagBadge key={tag.TagID} tag={tag} />)}
                        </div>
                      ) : <span className="text-slate-400">-</span>}
                    </td>
                    <td className="px-4 py-3">{element.MimeType || "-"}</td>
                    <td className="px-4 py-3">{formatSize(element.Taille)}</td>
                    <td className="px-4 py-3">{formatDate(element.CreateDate)}</td>
                    <td className="px-4 py-3">{element.Etat?.Nom || `#${element.EtatID}`}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handlePrivateDownload(element)}
                          className="inline-flex size-9 items-center justify-center rounded-lg border border-sky-300/40 text-sky-700 transition hover:bg-sky-500/10 dark:text-sky-200"
                          title="Télécharger"
                        >
                          <ArrowDownTrayIcon className="size-5" />
                        </button>
                        {element.EtatID === 1 && (
                          <button
                            type="button"
                            onClick={() => openShareModal(element)}
                            className="inline-flex size-9 items-center justify-center rounded-lg border border-violet-300/50 text-violet-700 transition hover:bg-violet-500/10 dark:text-violet-200"
                            title="Créer un lien de partage"
                          >
                            <ShareIcon className="size-5" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setTaggingElement(element)}
                          className="inline-flex size-9 items-center justify-center rounded-lg border border-violet-300/50 text-violet-700 transition hover:bg-violet-500/10 dark:text-violet-200"
                          title="Modifier mes tags"
                        >
                          <TagIcon className="size-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => openEditModal(element)}
                          className="inline-flex size-9 items-center justify-center rounded-lg border border-sky-300/40 text-sky-700 transition hover:bg-sky-500/10 dark:text-sky-200"
                          title="Modifier les données"
                        >
                          <PencilSquareIcon className="size-5" />
                        </button>
                        {element.EtatID === 2 ? (
                          <>
                            <button
                              type="button"
                              onClick={() => handleRestore(element.StockageElementID)}
                              className="inline-flex size-9 items-center justify-center rounded-lg border border-emerald-300/40 text-emerald-700 transition hover:bg-emerald-500/10 dark:text-emerald-200"
                              title="Restaurer"
                            >
                              <ArrowPathIcon className="size-5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => openHardDeleteModal(element)}
                              className="inline-flex size-9 items-center justify-center rounded-lg border border-red-400/50 text-red-700 transition hover:bg-red-500/10 dark:text-red-200"
                              title="Supprimer définitivement"
                            >
                              <XMarkIcon className="size-5" />
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleSoftDelete(element.StockageElementID)}
                            className="inline-flex size-9 items-center justify-center rounded-lg border border-red-300/40 text-red-700 transition hover:bg-red-500/10 dark:text-red-200"
                            title="Mettre en corbeille"
                          >
                            <TrashIcon className="size-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredElements.length > itemsPerPage && (
              <PaginationPage
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredElements.length}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
              />
            )}
          </>
        )}
      </section>

      <TagManagerDialog
        open={tagManagerOpen}
        onClose={() => setTagManagerOpen(false)}
        tags={tags}
        loading={tagsLoading}
        onChanged={handleTagsChanged}
      />

      <TagAssignmentDialog
        element={taggingElement}
        tags={tags}
        onClose={() => setTaggingElement(null)}
        onSaved={loadElements}
        onManageTags={openTagManagerFromAssignment}
      />

      <Dialog open={Boolean(sharingElement)} onClose={closeShareModal} className="relative z-50">
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 overflow-y-auto p-4 sm:flex sm:items-center sm:justify-center">
          <DialogPanel className="mx-auto w-full max-w-2xl overflow-hidden rounded-2xl border border-violet-400/20 bg-white shadow-2xl shadow-slate-950/20 dark:bg-slate-950">
            <div className="border-b border-violet-400/20 bg-gradient-to-r from-violet-500/15 via-sky-500/10 to-transparent px-6 py-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <DialogTitle className="text-xl font-black text-slate-950 dark:text-white">
                    Partager le fichier
                  </DialogTitle>
                  <p className="mt-1 text-sm font-semibold text-slate-500 dark:text-slate-400">
                    {sharingElement?.Nom}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeShareModal}
                  className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-500/10 dark:text-slate-300"
                  title="Fermer"
                >
                  <XMarkIcon className="size-5" />
                </button>
              </div>
            </div>

            <div className="grid gap-6 p-6">
              {shareLoading ? (
                <p className="text-sm font-semibold text-slate-500 dark:text-slate-300">Chargement du lien...</p>
              ) : (
                <>
                  {shareLink?.url && (
                    <div className="grid gap-4">
                      <div className="grid gap-3 sm:grid-cols-3">
                        <div className="rounded-xl border border-emerald-400/20 bg-emerald-500/10 p-4">
                          <p className="text-xs font-black uppercase text-emerald-700 dark:text-emerald-300">Disponibles</p>
                          <p className="mt-1 text-2xl font-black text-emerald-800 dark:text-emerald-200">
                            {shareLink.available} / {shareLink.maximum}
                          </p>
                        </div>
                        <div className="rounded-xl border border-sky-400/20 bg-sky-500/10 p-4">
                          <p className="text-xs font-black uppercase text-sky-700 dark:text-sky-300">Consommés</p>
                          <p className="mt-1 text-2xl font-black text-sky-800 dark:text-sky-200">{shareLink.consumed}</p>
                        </div>
                        <div className="rounded-xl border border-amber-400/20 bg-amber-500/10 p-4">
                          <p className="text-xs font-black uppercase text-amber-700 dark:text-amber-300">En attente</p>
                          <p className="mt-1 text-2xl font-black text-amber-800 dark:text-amber-200">{shareLink.pending}</p>
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-bold text-slate-700 dark:text-slate-200">Lien à partager</label>
                        <div className="mt-2 flex gap-2">
                          <input
                            type="text"
                            value={shareLink.url}
                            readOnly
                            onFocus={(event) => event.target.select()}
                            className="min-w-0 flex-1 rounded-xl border border-violet-400/20 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800 outline-none dark:bg-slate-900 dark:text-slate-100"
                          />
                          <button
                            type="button"
                            onClick={copyShareLink}
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-violet-700"
                          >
                            {shareCopied ? <CheckIcon className="size-5" /> : <ClipboardDocumentIcon className="size-5" />}
                            <span className="hidden sm:inline">{shareCopied ? "Copié" : "Copier"}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {shareLink?.destroyed && !shareLink.url && (
                    <div className="rounded-xl border border-emerald-400/25 bg-emerald-500/10 px-4 py-4 text-sm font-bold text-emerald-800 dark:text-emerald-200">
                      Le dernier téléchargement est terminé. Le quota est épuisé et le lien a été détruit.
                    </div>
                  )}

                  <form onSubmit={generateShareLink} className="grid gap-4 rounded-xl border border-slate-200/70 bg-slate-50/70 p-4 dark:border-slate-800 dark:bg-slate-900/60 sm:grid-cols-[1fr_auto] sm:items-end">
                    <div>
                      <label className="text-sm font-bold text-slate-700 dark:text-slate-200">
                        Nombre maximal de téléchargements
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="1000"
                        step="1"
                        value={shareMaximum}
                        onChange={(event) => setShareMaximum(event.target.value)}
                        className="mt-2 w-full rounded-xl border border-sky-500/20 bg-white px-4 py-3 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-sky-400 dark:bg-slate-950/70 dark:text-white"
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={shareSaving || Number(shareLink?.pending || 0) > 0}
                      className="rounded-xl bg-sky-500 px-5 py-3 text-sm font-bold text-white transition hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {shareSaving ? "Génération..." : shareLink?.url ? "Remplacer le lien" : "Générer le lien"}
                    </button>
                  </form>

                  {shareLink?.url && (
                    <button
                      type="button"
                      onClick={revokeShareLink}
                      disabled={shareSaving || Number(shareLink.pending || 0) > 0}
                      className="justify-self-start text-sm font-bold text-red-600 transition hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50 dark:text-red-300"
                    >
                      Détruire ce lien
                    </button>
                  )}

                  <div className="border-t border-slate-200/70 pt-5 dark:border-slate-800">
                    <h3 className="text-sm font-black uppercase text-slate-700 dark:text-slate-200">
                      Progression des téléchargements
                    </h3>
                    {shareDownloads.length === 0 ? (
                      <p className="mt-3 text-sm font-semibold text-slate-500 dark:text-slate-400">
                        Aucun téléchargement observé pendant l'ouverture de ce modal.
                      </p>
                    ) : (
                      <div className="mt-4 grid gap-3">
                        {shareDownloads.map((download) => (
                          <div key={download.downloadId} className="rounded-xl border border-slate-200/70 p-4 dark:border-slate-800">
                            <div className="mb-2 flex items-center justify-between gap-3 text-xs font-bold">
                              <span className="text-slate-600 dark:text-slate-300">
                                {download.status === "completed"
                                  ? "Téléchargement terminé"
                                  : download.status === "cancelled"
                                    ? "Téléchargement annulé — quota libéré"
                                    : "Téléchargement en cours — quota réservé"}
                              </span>
                              <span className="text-sky-600 dark:text-sky-300">{download.percent}%</span>
                            </div>
                            <div className="h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                              <div
                                className={`h-full rounded-full transition-all ${download.status === "cancelled" ? "bg-red-500" : download.status === "completed" ? "bg-emerald-500" : "bg-sky-500"}`}
                                style={{ width: `${download.percent}%` }}
                              />
                            </div>
                            <p className="mt-2 text-xs font-semibold text-slate-500 dark:text-slate-400">
                              {formatSize(download.transferredBytes)} / {formatSize(download.totalBytes)}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              {shareError && <p className="text-sm font-semibold text-red-600 dark:text-red-300">{shareError}</p>}
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      <Dialog open={Boolean(editingElement)} onClose={closeEditModal} className="relative z-50">
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-lg overflow-hidden rounded-2xl border border-sky-500/10 bg-white shadow-2xl shadow-slate-950/20 dark:bg-slate-950">
            <div className="border-b border-sky-500/10 bg-gradient-to-r from-sky-500/15 via-blue-500/10 to-transparent px-6 py-5">
              <DialogTitle className="text-xl font-black text-slate-950 dark:text-white">
                Modifier le fichier
              </DialogTitle>
              <p className="mt-1 text-sm font-semibold text-slate-500 dark:text-slate-400">
                #{editingElement?.StockageElementID}
              </p>
            </div>

            <form onSubmit={handleUpdateElement} className="grid gap-5 p-6">
              <div>
                <label className="text-sm font-bold text-slate-700 dark:text-slate-200">Nom</label>
                <input
                  type="text"
                  value={editForm.Nom}
                  onChange={(event) => setEditForm((current) => ({ ...current, Nom: event.target.value }))}
                  className="mt-2 w-full rounded-xl border border-sky-500/20 bg-white px-4 py-3 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-sky-400 dark:bg-slate-950/70 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-bold text-slate-700 dark:text-slate-200">Description</label>
                <textarea
                  value={editForm.Description}
                  onChange={(event) => setEditForm((current) => ({ ...current, Description: event.target.value }))}
                  className="mt-2 min-h-28 w-full rounded-xl border border-sky-500/20 bg-white px-4 py-3 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-sky-400 dark:bg-slate-950/70 dark:text-white"
                />
              </div>
              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeEditModal}
                  disabled={editSaving}
                  className="rounded-xl border border-slate-300/60 px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:text-slate-200"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={editSaving}
                  className="rounded-xl bg-sky-500 px-5 py-3 text-sm font-bold text-white transition hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {editSaving ? "Enregistrement..." : "Enregistrer"}
                </button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </Dialog>

      <Dialog open={Boolean(pendingHardDelete)} onClose={closeHardDeleteModal} className="relative z-50">
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md overflow-hidden rounded-2xl border border-red-400/20 bg-white shadow-2xl shadow-slate-950/20 dark:bg-slate-950">
            <div className="p-6">
              <div className="mb-5 inline-flex size-12 items-center justify-center rounded-xl bg-red-500/10 text-red-600 dark:text-red-300">
                <ExclamationTriangleIcon className="size-7" />
              </div>
              <DialogTitle className="text-xl font-black text-slate-950 dark:text-white">
                Supprimer définitivement ?
              </DialogTitle>
              <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
                Cette action supprimera la ligne en base et le dossier physique du fichier. Elle ne pourra pas être annulée.
              </p>
              <p className="mt-4 rounded-xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm font-bold text-red-700 dark:text-red-200">
                {pendingHardDelete?.Nom}
              </p>
              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeHardDeleteModal}
                  disabled={hardDeleting}
                  className="rounded-xl border border-slate-300/60 px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:text-slate-200"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={confirmHardDelete}
                  disabled={hardDeleting}
                  className="rounded-xl bg-red-600 px-5 py-3 text-sm font-bold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {hardDeleting ? "Suppression..." : "Supprimer définitivement"}
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  );
}
